import {
 useState
} from 'react';
import {
 Label
} from 'shared/components/ui/label';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
 ChevronDown, ChevronUp
} from 'lucide-react';
import OELCodeEditor, {
 OELContext
} from './OELCodeEditor';
import {
    ForController, RepeatUntilController, WhenController
} from 'builders/builderOpcode/jsonMode/types';

type StepControllersProps = {
    when?: WhenController;
    forCtrl?: ForController;
    repeatUntil?: RepeatUntilController;
    onWhenChange: (value: WhenController | undefined) => void;
    onForChange: (value: ForController | undefined) => void;
    onRepeatUntilChange: (value: RepeatUntilController | undefined) => void;
    oelContext?: OELContext;
};

const StepControllers = ({
    when,
    forCtrl,
    repeatUntil,
    onWhenChange,
    onForChange,
    onRepeatUntilChange,
    oelContext,
}: StepControllersProps) => {
    const ctx: OELContext = oelContext || {
 inputs: [],
previousStepIds: []
};
    const hasAnyController = !!when || !!forCtrl || !!repeatUntil;
    const [isOpen, setIsOpen] = useState(hasAnyController);

    return (
        <div className="border rounded-md">
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full flex justify-between items-center px-3 py-2"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="text-sm font-medium">Advanced Controllers</span>
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>

            {isOpen && (
                <div className="p-3 space-y-4 border-t">
                    {/* When Controller */}
                    <div className="space-y-2">
                        <div className="flex justify-between items-center">
                            <Label className="text-sm">When (Conditional Execution)</Label>
                            {when ? (
                                <Button type="button" size="sm" variant="ghost" onClick={() => onWhenChange(undefined)}>
                                    Remove
                                </Button>
                            ) : (
                                <Button type="button" size="sm" variant="ghost" onClick={() => onWhenChange('')}>
                                    Add
                                </Button>
                            )}
                        </div>
                        {when !== undefined && (
                            <OELCodeEditor
                                value={when}
                                onChange={(val) => onWhenChange(val)}
                                placeholder="@input.flag == true"
                                helpText="Step only executes if this condition is true"
                                oelContext={ctx}
                                templateMode={false}
                            />
                        )}
                    </div>

                    {/* For Controller */}
                    <div className="space-y-2">
                        <div className="flex justify-between items-center">
                            <Label className="text-sm">For (Loop)</Label>
                            {forCtrl ? (
                                <Button type="button" size="sm" variant="ghost" onClick={() => onForChange(undefined)}>
                                    Remove
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => onForChange({
 collection: '',
item: 'item'
})}
                                >
                                    Add
                                </Button>
                            )}
                        </div>
                        {forCtrl && (
                            <div className="space-y-2">
                                <OELCodeEditor
                                    label="Collection"
                                    value={forCtrl.collection}
                                    onChange={(val) => onForChange({
 ...forCtrl,
collection: val
})}
                                    placeholder="@input.items"
                                    helpText="Array to iterate over"
                                    oelContext={ctx}
                                    templateMode={false}
                                />
                                <div className="space-y-1">
                                    <Label className="text-sm">Item Variable Name</Label>
                                    <Input
                                        value={forCtrl.item}
                                        onChange={(e) => onForChange({
 ...forCtrl,
item: e.target.value
})}
                                        placeholder="item"
                                        className="font-mono text-sm"
                                    />
                                    <p className="text-xs text-muted-foreground">Name of the loop variable (accessible as @loop_var)</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Repeat Until Controller */}
                    <div className="space-y-2">
                        <div className="flex justify-between items-center">
                            <Label className="text-sm">Repeat Until</Label>
                            {repeatUntil ? (
                                <Button type="button" size="sm" variant="ghost" onClick={() => onRepeatUntilChange(undefined)}>
                                    Remove
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => onRepeatUntilChange({
 until: '',
max: 10
})}
                                >
                                    Add
                                </Button>
                            )}
                        </div>
                        {repeatUntil && (
                            <div className="space-y-2">
                                <OELCodeEditor
                                    label="Until Condition"
                                    value={repeatUntil.until}
                                    onChange={(val) => onRepeatUntilChange({
 ...repeatUntil,
until: val
})}
                                    placeholder="@step_1.output.done == true"
                                    helpText="Step repeats until this condition is true"
                                    oelContext={ctx}
                                    templateMode={false}
                                />
                                <div className="space-y-1">
                                    <Label className="text-sm">Max Iterations</Label>
                                    <Input
                                        type="number"
                                        value={repeatUntil.max}
                                        onChange={(e) => onRepeatUntilChange({
                                            ...repeatUntil,
                                            max: parseInt(e.target.value, 10) || 10
                                        })}
                                        min={1}
                                        className="w-32"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default StepControllers;
